/**
 * piezas.jsx — las piezas chicas que comparten el mes y el día.
 *
 * Nada de esto es genérico: son piezas de ESTE flujo, y viven juntas porque se
 * usan juntas. Lo genérico está en `ds.js` (componentes del DS) y en
 * `lib/yoolpay/` (formato, estados, catálogos).
 *
 * SIETE PIEZAS Y NO ONCE. `RailCierre`, `ModalConfirmacion`, `PanelBitacora` y
 * `BadgeSeveridad` eran del cierre MENSUAL de cinco pasos, que se borró al dejar
 * YoolPay en Mes → Día: el rail navegaba entre pasos que ya no existen y el modal
 * confirmaba la aprobación de un período. Se fueron con él.
 *
 * @see src/components/payments/yoolpay/cierre/ds.js
 */
import React from 'react';

// ---------------------------------------------------------------------------
// Aviso
// ---------------------------------------------------------------------------

const TONO = {
  alta: { borde: 'var(--badge-vencido-bg)', fondo: '#FFF7F9', color: 'var(--badge-vencido-text)' },
  media: { borde: '#EBC77A', fondo: '#FFFCF4', color: 'var(--badge-pendiente-text)' },
  baja: { borde: '#BFDBF7', fondo: '#F7FBFF', color: 'var(--badge-digital-text)' },
  ok: { borde: '#B9E2CD', fondo: '#F6FBF9', color: 'var(--badge-protegido-text)' },
};

/**
 * Aviso con severidad.
 *
 * La marca (`!` / `i` / `✓`) va SIEMPRE además del color: el color no puede ser el
 * único canal, porque un operador con daltonismo tiene que poder distinguir "hay
 * que actuar" de "es ruido".
 */
export function Aviso({ tono = 'baja', titulo, children, marca }) {
  const t = TONO[tono] ?? TONO.baja;
  const m = marca ?? (tono === 'ok' ? '✓' : tono === 'baja' ? 'i' : '!');
  return (
    <div
      style={{
        display: 'flex',
        gap: 11,
        alignItems: 'flex-start',
        padding: '13px 16px',
        border: `1px solid ${t.borde}`,
        background: t.fondo,
        borderRadius: 12,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          flexShrink: 0,
          width: 18,
          height: 18,
          borderRadius: '50%',
          background: t.color,
          color: '#fff',
          fontSize: 11,
          fontWeight: 800,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: 1,
        }}
      >
        {m}
      </span>
      <span style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--yool-gray1)' }}>
        {titulo ? <strong style={{ color: 'var(--yool-black)' }}>{titulo} </strong> : null}
        {children}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Estructura
// ---------------------------------------------------------------------------

/** Tarjeta con encabezado. `borde` deja marcar la sección que no cuadra. */
export function Seccion({ titulo, bajada, acciones, borde, children, derecha }) {
  return (
    <section
      style={{
        background: '#fff',
        border: `1px solid ${borde ?? 'var(--yool-border)'}`,
        borderRadius: 16,
        overflow: 'hidden',
      }}
    >
      {(titulo || bajada || acciones || derecha) && (
        <header
          style={{
            padding: '19px 24px 15px',
            borderBottom: '1px solid var(--yool-gray)',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: 20,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ minWidth: 0 }}>
            {titulo ? (
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, letterSpacing: '-.02em' }}>
                {titulo}
              </h2>
            ) : null}
            {bajada ? (
              <p
                style={{
                  margin: '6px 0 0',
                  fontSize: 13,
                  color: 'var(--yool-muted)',
                  lineHeight: 1.5,
                  maxWidth: 700,
                }}
              >
                {bajada}
              </p>
            ) : null}
          </div>
          {derecha ?? (acciones ? <div style={{ display: 'flex', gap: 9 }}>{acciones}</div> : null)}
        </header>
      )}
      {children}
    </section>
  );
}

/** Etiqueta de columna / dato: 10px, mayúsculas, tracking. */
export function Rotulo({ children, align }) {
  return (
    <span
      style={{
        display: 'block',
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '.1em',
        textTransform: 'uppercase',
        color: 'var(--yool-muted)',
        textAlign: align,
      }}
    >
      {children}
    </span>
  );
}

/** Cifra tabular, para que las columnas de plata se alineen. */
export function Cifra({ children, size = 13, color, weight = 600, align = 'right' }) {
  return (
    <span
      style={{
        display: 'block',
        fontVariantNumeric: 'tabular-nums',
        fontSize: size,
        fontWeight: weight,
        color: color ?? 'var(--yool-black)',
        textAlign: align,
        letterSpacing: '-.01em',
      }}
    >
      {children}
    </span>
  );
}

/** Encabezado de la grilla. */
export function Encabezado({ cols, children }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: cols,
        gap: 12,
        padding: '10px 24px',
        background: '#FAFAF9',
        borderBottom: '1px solid var(--yool-gray)',
      }}
    >
      {children}
    </div>
  );
}

/** Fila de grilla con las mismas medidas que el encabezado. */
export function Fila({ cols, children, destacada, tenue, alinear = 'center' }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: cols,
        gap: 12,
        alignItems: alinear,
        padding: '13px 24px',
        borderBottom: '1px solid var(--yool-gray)',
        background: destacada ? '#FFF7F9' : undefined,
        opacity: tenue ? 0.62 : 1,
      }}
    >
      {children}
    </div>
  );
}

/** Pie de sección: el resumen en prosa y, opcional, un total. */
export function Pie({ children, total, totalLabel, totalColor }) {
  return (
    <div
      style={{
        padding: '15px 24px',
        background: '#FAFAF9',
        borderTop: '1px solid var(--yool-gray)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 18,
        flexWrap: 'wrap',
      }}
    >
      <span style={{ fontSize: 12.5, color: 'var(--yool-gray1)' }}>{children}</span>
      {total != null ? (
        <span style={{ display: 'flex', alignItems: 'baseline', gap: 9 }}>
          <Rotulo>{totalLabel}</Rotulo>
          <span
            style={{
              fontSize: 17,
              fontWeight: 700,
              fontVariantNumeric: 'tabular-nums',
              color: totalColor ?? 'var(--yool-black)',
            }}
          >
            {total}
          </span>
        </span>
      ) : null}
    </div>
  );
}
